import React, { useEffect, useId, useRef, useState } from 'react';
import { FiChevronDown, FiUser, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ref, update } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebaseConfig';
import { regenerateScoutFromCv } from '../scoutApi';
import { profileHasCv } from './opportunityData';

// Preserve the in-progress onboarding refinements and existing CV upload flow.
export default function OpportunityProfilePrompt({ user, profile, dismissed, onDismiss }) {
  const navigate = useNavigate();
  const input = useRef(null);
  const container = useRef(null);
  const trigger = useRef(null);
  const popupId = useId();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [notice, setNotice] = useState('');
  const scoutId = profile?.primaryScoutId;
  const locations = profile?.jobScouts?.[scoutId]?.locationPreferences;
  const missingStates = locations?.targetCountries?.includes('United States') && !locations?.targetStates?.length && !locations?.targetLocations?.some((item) => item.country === 'United States' && item.state);
  const missingDetails = !dismissed && (missingStates || locations?.workAuthorizationStatus === 'unknown');
  const missingCv = user && !uploaded && !profileHasCv(profile, scoutId);
  const close = () => { setOpen(false); trigger.current?.focus({ preventScroll: true }); };
  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => { if (!container.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current?.focus({ preventScroll: true }); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { setNotice('Please choose a PDF.'); return; }
    setUploading(true);
    setNotice('');
    try {
      const path = `Images/${user.uid}/cv.pdf`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, file, { contentType: 'application/pdf' });
      const downloadUrl = await getDownloadURL(fileRef);
      await update(ref(db, `people/${user.uid}`), {
        cv: { ...(profile?.cv || {}), Link: downloadUrl }, cvUrl: downloadUrl,
        cvMetadata: { name: file.name, size: file.size, type: 'application/pdf', storagePath: path, downloadUrl, capturedAt: Date.now() },
      });
      setUploaded(true);
      if (scoutId) await regenerateScoutFromCv({ user, scoutId });
      setNotice('CV uploaded. Your matches are being refreshed.');
    } catch { setNotice('We couldn’t complete the upload or refresh. Please try again.'); }
    finally { setUploading(false); }
  };
  if (!missingDetails && !missingCv && !notice) return null;
  return <div className="opp-profile-completion" ref={container}>
    <button ref={trigger} type="button" className="opp-profile-trigger" aria-expanded={open} aria-haspopup="dialog" aria-controls={popupId} onClick={() => setOpen(!open)}><FiUser aria-hidden="true" />Complete details<FiChevronDown aria-hidden="true" /></button>
    {open && <div className="opp-profile-popover" id={popupId} role="dialog" aria-labelledby={`${popupId}-title`}>
      <header><h2 id={`${popupId}-title`}>Complete your details</h2><button type="button" className="opp-profile-close" aria-label="Close profile details" onClick={close}><FiX aria-hidden="true" /></button></header>
      {missingDetails && <div className="opp-profile-prompt"><p>{missingStates ? 'Choose your target US states to improve your matches.' : 'Add your work authorization to improve your matches.'}</p><div className="opp-profile-actions"><button type="button" className="opp-button" onClick={() => navigate('/scout-details')}>Add details</button><button type="button" className="opp-text-link" onClick={() => { onDismiss(); close(); }}>Later</button></div></div>}
      {missingCv && <div className="opp-profile-prompt"><p>Add your CV for more relevant matches.</p><div className="opp-profile-actions"><button type="button" className="opp-button" disabled={uploading} onClick={() => input.current?.click()}>{uploading ? 'Uploading…' : 'Upload CV'}</button><button type="button" className="opp-text-link" onClick={() => navigate('/ask-anything', { state: { prefill: 'Help me build a CV from scratch.' } })}>Build with Career Coach</button></div></div>}
      {notice && <p className="opp-muted opp-small" role="status">{notice}</p>}
    </div>}
    {missingCv && <input ref={input} type="file" hidden accept=".pdf,application/pdf" onChange={upload} />}
  </div>;
}
